import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';

import { I18nService } from '@personal-area/global/i18n';
import { isConnectivityFailed } from '@personal-area/global/http/services';
import { ApiException, ApiMapException } from '@personal-area/global/form-validation/models';
import { ErrorPopupComponent } from '@personal-area/global/ui';

@Injectable({ providedIn: 'root' })
export class ApiFormService {
  private readonly _dialog = inject(MatDialog);
  private readonly _translateService = inject(TranslateService);
  private _i18nService = inject(I18nService);

  /**
   * Метод для заполнения формы ошибка из ответа API
   * @param {ApiMapException} ex
   * @param {FormGroup} form
   * @return boolean
   */
  public static fillForm(ex: ApiMapException, form: UntypedFormGroup): boolean {
    let changed = false;

    if (form) {
      form.markAsPending();
      form.markAsTouched();

      Object.keys(ex.errors).forEach((value) => {
        if (form.controls.hasOwnProperty(value)) {
          changed = true;
          const ctrl = form.get(value) || form.controls[value];
          ctrl.setErrors({ api: ex.errors[value][0] });
          ctrl.markAsTouched();
        }
      });
    }

    return changed;
  }

  /**
   * Метод для получения первой ошибки из ответа
   * @param {HttpErrorResponse} err
   * @param addToken
   * @return {string}
   */
  public static firstError(err: HttpErrorResponse, addToken = true): string {
    let errorMessage = err.message;
    let ex = err.error.exception || err.error.mapException;
    if (ex) {
      if (ex.hasOwnProperty('errors')) {
        ex = ex as ApiMapException;
        Object.keys(ex.errors).forEach((value) => {
          const { error, tMessage } = ex.errors[value][0];
          errorMessage =
            (addToken ? ex.token + ': ' : '') + (tMessage || error);
        });
      } else {
        ex = ex as ApiException;
        errorMessage = (addToken ? ex.token + ': ' : '') + ex.error.tMessage;
      }
    }
    return errorMessage;
  }

  public defaultFormError(
    err: HttpErrorResponse,
    form: UntypedFormGroup = null,
  ): void {
    if (isConnectivityFailed(err)) {
      return;
    }
    const ex = err.error.exception || err.error.mapException;
    if (ex && ex.hasOwnProperty('errors')) {
      if (!ApiFormService.fillForm(ex, form)) {
        this._dialog.open(ErrorPopupComponent, {
          data: { token: ex.token },
          panelClass: 'regular-popup-container',
          direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
        });
      }
    } else if (ex && ex.hasOwnProperty('token') && ex.token !== 'no-token') {
      this._dialog.open(ErrorPopupComponent, {
        data: { token: ex.token },
        panelClass: 'regular-popup-container',
        direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      });
    } else {
      this._dialog.open(ErrorPopupComponent, {
        panelClass: 'regular-popup-container',
        direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      });
    }
  }

  public defaultError(
    err: HttpErrorResponse,
    form: UntypedFormGroup = null,
    isForm = false,
  ): Observable<string> {
    let ex = err.error.exception || err.error.mapException;
    if (!ex) {
      return of(undefined);
    }

    if (
      isForm &&
      ex.hasOwnProperty('errors') &&
      !ApiFormService.fillForm(ex, form)
    ) {
      return of(ApiFormService.firstError(err));
    } else {
      ex = ex as ApiException;
      return this._translateService.get('common.error.unexpected-exception', {
        token: ex.token,
      });
    }
  }

  public openErrorPopup(): void {
    this._dialog.open(ErrorPopupComponent, {
      panelClass: 'regular-popup-container',
      direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
    });
  }
}
